import { z } from 'zod';

/**
 * Plaques d'immatriculation (décision du porteur, vague 4).
 * - `fr` : plaque française, SIV (`AA-123-AA`) ou ancien FNI (`123 ABC 45`), normalisée ;
 * - `other` : plaque étrangère ou spéciale (lettres sans accent, chiffres, séparateurs simples).
 * Fonctions pures, partagées par l'API (validation d'entrée) et l'interface (saisie).
 */
export const PlateKind = z.enum(['fr', 'other']);
export type PlateKind = z.infer<typeof PlateKind>;

export type PlateResult = { ok: true; value: string } | { ok: false; message: string };

export const PLATE_FR_ERROR =
  'Plaque française invalide (formats AA-123-AA ou 123 ABC 45). Pour une plaque étrangère ou spéciale, cochez la case correspondante.';
export const PLATE_OTHER_ERROR =
  'Plaque invalide : lettres sans accent, chiffres, et espaces ou tirets simples entre les groupes (2 à 15 caractères).';

/** SIV : lettres sans I, O ni U ; 001 à 999. */
const SIV = /^([A-HJ-NP-TV-Z]{2})(\d{3})([A-HJ-NP-TV-Z]{2})$/;
/** FNI : 1 à 4 chiffres, 1 à 3 lettres, département (01 à 95, 2A, 2B, 971 à 976). */
const FNI = /^(\d{1,4})([A-Z]{1,3})(0[1-9]|[1-8]\d|9[0-5]|2A|2B|97[1-6])$/;
/** Plaque étrangère ou spéciale : groupes alphanumériques séparés par un espace ou un tiret simple. */
const OTHER = /^[A-Z0-9]+(?:[ -][A-Z0-9]+)*$/;

/** Plaque française : SIV ou FNI, sinon erreur. */
function normalizeFr(upper: string): PlateResult {
  const compact = upper.replace(/[\s-]/g, '');
  const siv = SIV.exec(compact);
  if (siv) {
    const [, left, digits, right] = siv as unknown as [string, string, string, string];
    if (digits === '000' || left === 'SS') return { ok: false, message: PLATE_FR_ERROR };
    return { ok: true, value: `${left}-${digits}-${right}` };
  }
  const fni = FNI.exec(compact);
  if (fni) {
    const [, digits, letters, dept] = fni as unknown as [string, string, string, string];
    if (/^0+$/.test(digits)) return { ok: false, message: PLATE_FR_ERROR };
    return { ok: true, value: `${digits} ${letters} ${dept}` };
  }
  return { ok: false, message: PLATE_FR_ERROR };
}

function normalizeOther(upper: string): PlateResult {
  const value = upper.replace(/\s+/g, ' ');
  if (!OTHER.test(value)) return { ok: false, message: PLATE_OTHER_ERROR };
  const useful = value.replace(/[ -]/g, '').length;
  if (useful < 2 || useful > 15) return { ok: false, message: PLATE_OTHER_ERROR };
  return { ok: true, value };
}

/**
 * Normalise une plaque saisie. Toujours : majuscules ; un accent ou un symbole rend la plaque invalide
 * (aucune translittération). Les espaces et tirets sont ignorés pour l'analyse d'une plaque française.
 */
export function normalizePlate(kind: PlateKind, raw: string): PlateResult {
  const upper = raw.trim().toUpperCase();
  return kind === 'fr' ? normalizeFr(upper) : normalizeOther(upper);
}

/** Forme compacte (lettres et chiffres seuls), utilisée pour l'unicité d'une plaque dans un garage. */
export function compactPlate(plate: string): string {
  return plate.toUpperCase().replace(/[^A-Z0-9]/g, '');
}
